import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Keyboard } from "react-native";
import { createParam } from "solito";
import { useRouter } from "solito/router";
import type { z } from "zod";
import { Lock as LockIcon } from "@tamagui/lucide-icons";
import type { AuthenticateResultErrorType } from "@medbillai/graphql-types";
import { applyFormErrors, FullscreenSpinner, Headline1, isWeb, OAuthButtons, OAuthDivider, type OAuthProviderType, Paragraph2, ParagraphSmall, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { verifyEmailUrl } from "@medbillai/utils/urls";
import { GrantedLogo } from "../../components/local-images";
import { ScreenViewCenter, ScreenViewTopThird } from "../../components/ScreenView";
import { analyticsService } from "../../lib/analytics";
import { useAuth } from "../../lib/auth";
import { useBoolVariation } from "../../lib/flag";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import { SingularEvents, singularService } from "../../lib/singular";
import { useAlerts } from "../../provider/alerts";
import { useBotControl } from "./components/BotControlProvider";
import { SignInForm, type SignInSchema } from "./components/SignInForm";
export type LoginParams = {
  forceLogout?: boolean;
  authError?: AuthenticateResultErrorType;
};
const {
  useParams
} = createParam<LoginParams>();
const errorMessages: Record<AuthenticateResultErrorType, string> = {
  INVALID_PKCE_CODE_CHALLENGE: "The login link you followed was requested from a different device. For your security, please request log in again from this device.",
  UNABLE_TO_AUTH_MAGIC_LINK: "The login link you followed has expired or has already been used. Please try logging in again.",
  INVALID_AUTH_OAUTH: "Your auth provider is not working, please try again with your email.",
  UNABLE_TO_AUTH_OAUTH: "Your auth provider is not working, please try again with your email.",
  UNKNOWN_ERROR: "Something went wrong, please try again"
};
const ScreenView = isWeb ? ScreenViewCenter : ScreenViewTopThird;
export const LoginScreen = () => {
  const {
    params
  } = useParams();
  const {
    push,
    replace
  } = useRouter();
  const {
    showAlert
  } = useAlerts();
  const {
    isReady,
    isAuthenticated,
    sendMagicLink,
    logout,
    user,
    startOAuth,
    getAndClearIntendedPath
  } = useAuth();
  const isSignUpOnboarding = useSignUpOnboardingStore(state => state.isSignUpOnboarding);
  const [forceEnable, setForceEnable] = useState(false);
  const {
    getBotControlToken,
    resetBotControl,
    isBotControlEnabled
  } = useBotControl();
  const isOAuthEnabled = useBoolVariation(Flag.OAUTH_ENABLED, false);
  let errorMessage: string | undefined;
  if (params?.authError) {
    errorMessage = errorMessages[params?.authError] ?? errorMessages.UNKNOWN_ERROR;
  }
  const form = useForm<z.infer<typeof SignInSchema>>();
  const loggedOut = useRef(false);
  useEffect(() => {
    if (params?.forceLogout && !loggedOut.current) {
      loggedOut.current = true;
      void logout();
    }
  }, [params?.forceLogout, logout]);
  useEffect(() => {
    if (user?.profile?.email) {
      // Should dirty does not work on web.. so we need to force the form to be
      // enabled.
      form.setValue("email", user.profile.email, {
        shouldDirty: true
      });
      if (isWeb) {
        setForceEnable(true);
      }
    }
  }, [form, user?.profile?.email]);
  const doSendMagicLink = useCallback(async ({
    email
  }: z.infer<typeof SignInSchema>) => {
    try {
      const botControlToken = getBotControlToken();
      // If bot control is enabled, a valid token is required.
      if (isBotControlEnabled && !botControlToken) {
        showAlert({
          type: "error",
          message: "Please verify you are human."
        });
        return;
      }
      const userSegmentationData = useSignUpOnboardingStore.getState().userSegmentationData;
      const result = await sendMagicLink({
        email: email,
        botControlToken: botControlToken,
        userSegmentationData
      });
      if (applyFormErrors(form, result)) {
        return;
      }
      // Sent email successfully
      Keyboard.dismiss();
      if (result.__typename === "SendMagicLinkResponse") {
        singularService.setUser({
          id: result.user.id,
          email
        });
        if (result.created) {
          analyticsService.trackEvent("UserCreated", {
            email
          });
          singularService.event(SingularEvents.completeRegistration);
        }
      }
      analyticsService.trackEvent("SubmitLogin", {
        method: "EMAIL"
      });
      push(verifyEmailUrl(email));
    } catch (error) {
      console.error("Error while sending magic link", error);
      showAlert({
        type: "error",
        message: "Unable to send a login link, please try again."
      });

      // If bot control is enabled, reset the token and component
      if (isBotControlEnabled) {
        resetBotControl();
      }
    }
  }, [sendMagicLink, form, showAlert, push, isBotControlEnabled, getBotControlToken, resetBotControl]);
  const handleOAuthPress = useCallback(async (provider: OAuthProviderType) => {
    try {
      await startOAuth(provider);
    } catch (error) {
      console.error("Error during OAuth authentication", error);
      showAlert({
        type: "error",
        message: "Unable to authenticate with OAuth, please try again."
      });
    }
  }, [startOAuth, showAlert]);
  useEffect(() => {
    // If the user is already logged in and they are not force logging out,
    // redirect to the home page.
    if (isAuthenticated && !params?.forceLogout && isReady) {
      // Check for stored intended path first, fallback to home
      const intendedPath = getAndClearIntendedPath();
      replace(intendedPath || "/");
    }
  }, [isAuthenticated, params?.forceLogout, replace, isReady, getAndClearIntendedPath]);
  if (!isReady || isAuthenticated && !params?.forceLogout && isReady) {
    return <FullscreenSpinner />;
  }
  return <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-component="LoginScreen" data-sentry-source-file="login.tsx">
      <ScreenView backgroundRadialGradient={!isWeb} centerHorizontal stackProps={{
      "$platform-web": {
        // Offset the screen to account for the header
        mt: -100
      }
    }} extraPadding={!isWeb} disableScroll widthLimit={500} renderAfter={<YStack position="absolute" bottom={0} left={0} right={0} justifyContent="center" alignItems="center" gap="$2" $platform-web={{
      paddingBottom: "$4.5"
    }}>
            <LockIcon color="$textSecondary" size={12} strokeWidth={2.5} />
            <ParagraphSmall theme="alt1" maxWidth={260} textAlign="center">
              Granted keeps your data private, secure, and fully encrypted.
            </ParagraphSmall>
          </YStack>} data-sentry-element="ScreenView" data-sentry-source-file="login.tsx">
        <YStack flex={1} maxHeight={100} $platform-web={{
        maxHeight: 0
      }} data-sentry-element="YStack" data-sentry-source-file="login.tsx" />
        <YStack justifyContent="center" alignItems="center" gap="$5.5" paddingBottom="$6" data-sentry-element="YStack" data-sentry-source-file="login.tsx">
          <GrantedLogo height={76} width={76} data-sentry-element="GrantedLogo" data-sentry-source-file="login.tsx" />
          <Headline1 maxWidth={240} textAlign="center" pt="$2" data-sentry-element="Headline1" data-sentry-source-file="login.tsx">
            {isSignUpOnboarding ? `Create an account` : "Welcome back"}
          </Headline1>
        </YStack>
        {errorMessage ? <Paragraph2 color="$red10">{errorMessage}</Paragraph2> : null}
        <YStack flex={1} $platform-web={{
        flex: 0
      }} data-sentry-element="YStack" data-sentry-source-file="login.tsx">
          {isOAuthEnabled ? <>
              <OAuthButtons onPress={handleOAuthPress} />
              <OAuthDivider />
            </> : null}
          <SignInForm form={form} onSubmit={doSendMagicLink} forceEnable={forceEnable} data-sentry-element="SignInForm" data-sentry-source-file="login.tsx" />
        </YStack>
      </ScreenView>
    </FormProvider>;
};